import { ReactNode, useState } from 'react';

import { type ColorToken } from '@backrs_ui/common-ui';

import { Box, ButtonBase, Chip, Popover, Radio, SxProps, Theme, Typography } from '@mui/material';

export type ChipTone = Readonly<{
  text: ColorToken;
  border: ColorToken;
  fill: ColorToken;
  dot: ColorToken;
}>;

export const CHIP_TONES = {
  neutral: { text: 'Gray/700', border: 'Gray/200', fill: 'Gray/50', dot: 'Gray/500' },
  brand: { text: 'Brand/700', border: 'Brand/200', fill: 'Brand/50', dot: 'Brand/500' },
  success: { text: 'Success/700', border: 'Success/200', fill: 'Success/50', dot: 'Success/500' },
  warning: { text: 'Warning/700', border: 'Warning/200', fill: 'Warning/50', dot: 'Warning/500' },
  error: { text: 'Error/700', border: 'Error/200', fill: 'Error/50', dot: 'Error/500' },
} as const satisfies Record<string, ChipTone>;

export type ChipOption<T extends string> = Readonly<{ value: T; label: string; tone?: ChipTone }>;

const ToneChip = ({ label, tone }: Readonly<{ label: string; tone: ChipTone }>) => (
  <Chip label={label} variant="pill" size="sm" sx={chipSx(tone)} />
);

type OptionChipProps<T extends string> = Readonly<{
  id: string;
  value: T;
  options: readonly ChipOption<T>[];
  onChange: (value: T) => void;
  trigger?: ReactNode;
  triggerSx?: SxProps<Theme>;
}>;

export const OptionChip = <T extends string>({
  id,
  value,
  options,
  onChange,
  trigger,
  triggerSx: customTriggerSx,
}: OptionChipProps<T>) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const current = options.find((option) => option.value === value) ?? options[0];

  const choose = (option: ChipOption<T>) => {
    setAnchorEl(null);
    if (option.value !== value) onChange(option.value);
  };

  const renderOption = (option: ChipOption<T>) =>
    option.tone ? (
      <ToneChip label={option.label} tone={option.tone} />
    ) : (
      <Typography variant="text-sm-regular">{option.label}</Typography>
    );

  return (
    <Box>
      <ButtonBase
        id={id}
        disableRipple
        aria-haspopup="menu"
        onClick={(event) => setAnchorEl(event.currentTarget)}
        sx={
          [
            triggerSx,
            ...(Array.isArray(customTriggerSx) ? customTriggerSx : [customTriggerSx]),
          ] as SxProps<Theme>
        }
      >
        {trigger ?? renderOption(current)}
      </ButtonBase>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        PaperProps={{ sx: popoverSx }}
      >
        <Box role="menu" sx={optionsSx}>
          {options.map((option) => (
            <ButtonBase
              key={option.value || 'none'}
              id={`${id}-option-${option.value || 'none'}`}
              component="div"
              disableRipple
              role="menuitemradio"
              aria-checked={option.value === value}
              sx={optionSx}
              onClick={() => choose(option)}
            >
              <Radio checked={option.value === value} tabIndex={-1} />
              {renderOption(option)}
            </ButtonBase>
          ))}
        </Box>
      </Popover>
    </Box>
  );
};

const chipSx =
  (tone: ChipTone): SxProps<Theme> =>
  ({ backrs }) => ({
    color: backrs.colors[tone.text],
    border: `1px solid ${backrs.colors[tone.border]}`,
    backgroundColor: backrs.colors[tone.fill],
  });

const triggerSx: SxProps<Theme> = {
  display: 'inline-flex',
  textAlign: 'inherit',
  cursor: 'pointer',
};

const popoverSx: SxProps<Theme> = ({ backrs }) => ({
  boxShadow: backrs.shadows['shadow-md'],
  borderRadius: backrs.radius['radius-md'],
  backgroundColor: backrs.colors['Colors/Background/bg-primary'],
  border: `1px solid ${backrs.colors['Colors/Base/Black']}`,
});

const optionsSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  paddingY: backrs.spacing['spacing-md'],
  paddingRight: backrs.spacing['spacing-4xl'],
});

const optionSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  gap: backrs.spacing['spacing-md'],
  paddingLeft: backrs.spacing['spacing-xl'],
  paddingTop: backrs.spacing['spacing-md'],
  cursor: 'pointer',
  userSelect: 'none',
  '& .MuiRadio-root': { margin: backrs.spacing['spacing-none'] },
});
